import React, { useState } from 'react';
import { ScenarioAnswers, CameraModel } from '../types/camera';
import NDFilterSelector from './NDFilterSelector';

interface QuestionFlowProps {
  onComplete: (answers: ScenarioAnswers) => void;
  isLoading: boolean;
  onLogout?: () => void;
}

const QuestionFlow: React.FC<QuestionFlowProps> = ({ onComplete, isLoading, onLogout }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<ScenarioAnswers>>({});

  const questions = [
    {
      key: 'cameraModel' as keyof ScenarioAnswers,
      question: 'Which camera are you using?',
      options: [
        { value: { brand: 'GoPro', model: 'Hero 13' } as CameraModel, label: 'GoPro Hero 13' },
        { value: { brand: 'GoPro', model: 'Hero 11' } as CameraModel, label: 'GoPro Hero 11' },
        { value: { brand: 'DJI', model: 'OSMO Action 5 Pro' } as CameraModel, label: 'DJI OSMO Action 5 Pro' }
      ]
    },
    {
      key: 'activity' as keyof ScenarioAnswers,
      question: 'What activity will you be filming?',
      type: 'text',
      placeholder: 'e.g., skiing, mountain biking, surfing, travel vlog'
    },
    {
      key: 'environment' as keyof ScenarioAnswers,
      question: 'What environment will you be filming in?',
      options: [
        { value: 'outdoor', label: 'Outdoor (General/Overcast)' },
        { value: 'indoor', label: 'Indoor' },
        { value: 'underwater', label: 'Underwater' },
        { value: 'low-light', label: 'Low Light/Dusk' },
        { value: 'bright-sun', label: 'Bright Direct Sunlight' }
      ]
    },
    {
      key: 'lighting' as keyof ScenarioAnswers,
      question: 'What type of lighting will you have?',
      options: [
        { value: 'natural', label: 'Natural Daylight' },
        { value: 'golden-hour', label: 'Golden Hour/Sunset' },
        { value: 'fluorescent', label: 'Fluorescent Lights' },
        { value: 'led-daylight', label: 'LED Daylight (5600K)' },
        { value: 'led-tungsten', label: 'LED Tungsten/Warm (3200K)' },
        { value: 'incandescent', label: 'Incandescent/Tungsten Bulbs' },
        { value: 'mixed', label: 'Mixed Lighting Sources' },
        { value: 'night', label: 'Night/Very Dark' }
      ]
    },
    {
      key: 'movement' as keyof ScenarioAnswers,
      question: 'How much action/movement will your subject have?',
      options: [
        { value: 'static', label: 'Static/Stationary (talking, sitting)' },
        { value: 'slow', label: 'Slow Movement (walking, casual activity)' },
        { value: 'moderate', label: 'Moderate Action (jogging, casual sports)' },
        { value: 'fast', label: 'Fast Action (running, biking, skiing)' },
        { value: 'extreme', label: 'Extreme Sports (motocross, surfing, skydiving)' }
      ]
    },
    {
      key: 'mounting' as keyof ScenarioAnswers,
      question: 'How will the camera be mounted/held during filming?',
      options: [
        { value: 'tripod', label: 'Tripod/Stationary Mount' },
        { value: 'handheld', label: 'Handheld' },
        { value: 'selfie-stick', label: 'Selfie Stick/Monopod' },
        { value: 'helmet', label: 'Helmet Mount' },
        { value: 'chest-mount', label: 'Chest/Body Mount' },
        { value: 'vehicle-mount', label: 'Car/Motorcycle Mount' },
        { value: 'bike-mount', label: 'Bike/Handlebar Mount' },
        { value: 'gimbal', label: 'Gimbal/Stabilizer' },
        { value: 'drone', label: 'Drone/Aerial Mount' },
        { value: 'other', label: 'Other/Mixed Mounting' }
      ]
    },
    {
      key: 'priority' as keyof ScenarioAnswers,
      question: 'What\'s most important to you?',
      options: [
        { value: 'quality', label: 'Best Video Quality' },
        { value: 'stabilization', label: 'Maximum Stabilization' },
        { value: 'battery-life', label: 'Longest Battery Life' },
        { value: 'storage', label: 'Efficient Storage Usage' }
      ]
    },
    {
      key: 'duration' as keyof ScenarioAnswers,
      question: 'How long will you be filming?',
      options: [
        { value: 'short', label: 'Short clips (under 10 minutes)' },
        { value: 'medium', label: 'Medium sessions (10-30 minutes)' },
        { value: 'long', label: 'Long sessions (30+ minutes)' },
        { value: 'all-day', label: 'All day filming' }
      ]
    },
    {
      key: 'experience' as keyof ScenarioAnswers,
      question: 'What\'s your experience level with action cameras?',
      options: [
        { value: 'beginner', label: 'Beginner - I want simple settings' },
        { value: 'intermediate', label: 'Intermediate - I know some basics' },
        { value: 'advanced', label: 'Advanced - I understand manual controls' }
      ]
    }
  ];

  const currentQuestion = questions[currentStep];

  const handleAnswer = (value: any) => {
    const newAnswers = { ...answers, [currentQuestion.key]: value };
    setAnswers(newAnswers);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Move to ND filter selection
      setCurrentStep(currentStep + 1);
    }
  };

  const handleNDFilterComplete = (filters: string[]) => {
    const finalAnswers = { ...answers, ndFilters: filters } as ScenarioAnswers;
    onComplete(finalAnswers);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLogout = () => {
    // Mock logout for testing mode
    if (onLogout) {
      onLogout();
    }
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const value = formData.get(currentQuestion.key) as string;
    if (value.trim()) {
      handleAnswer(value.trim());
    }
  };

  // Check if we're on the ND filter step
  const isNDFilterStep = currentStep >= questions.length;
  const totalSteps = questions.length + 1; // Add 1 for ND filter step

  return (
    <div className="question-flow">
      {onLogout && (
        <div className="question-flow-header">
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      )}
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        ></div>
      </div>
      
      {isNDFilterStep ? (
        <div className="question-container">
          <h2>Step {currentStep + 1} of {totalSteps}</h2>
          <NDFilterSelector
            onComplete={handleNDFilterComplete}
            onBack={handleBack}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <div className="question-container">
          <h2>Step {currentStep + 1} of {totalSteps}</h2>
          <h3>{currentQuestion.question}</h3>
        
        {currentQuestion.type === 'text' ? (
          <form onSubmit={handleTextSubmit}>
            <input
              type="text"
              name={currentQuestion.key}
              placeholder={currentQuestion.placeholder}
              required
              disabled={isLoading}
            />
            <div className="button-group">
              {currentStep > 0 && (
                <button type="button" onClick={handleBack} disabled={isLoading}>
                  Back
                </button>
              )}
              <button type="submit" disabled={isLoading}>
                Next
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="options">
              {currentQuestion.options?.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option.value)}
                  className="option-button"
                  disabled={isLoading}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {currentStep > 0 && (
              <button 
                onClick={handleBack} 
                className="back-button"
                disabled={isLoading}
              >
                Back
              </button>
            )}
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default QuestionFlow;